import * as React from "react";
import { addYears } from "../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { IsaHelper } from "../../Helpers/IsaHelper";
import { IsaContents } from "../Resource/Contents";
import * as classNames from "classNames";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CashPayment } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Payment/CashPayment";
import { Action } from "../../../../../Actions/Action";
import { CashPaymentTypeQuery } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Payment/CashPaymentTypeQuery";
import { CashPaymentFrequencyQuery } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Payment/CashPaymentFrequencyQuery";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";

interface IIsaAllowanceProps extends IProcessComponentProps {
    cssWrapper?: string;
    isRegular?: boolean;
}

interface IIsaAllowanceState {
    existingRegularPayment: CashPayment[];
}

export class IsaAllowance extends ProcessComponent<IIsaAllowanceProps, IIsaAllowanceState> {
    constructor(props: any) {
        super(props);
        this.state = { existingRegularPayment: [] };
    }

    componentDidMount(): void {
        super.componentDidMount();

        if (!CommonHelper.isNullOrEmpty(getQueryParameters()["subaccounthierarchyid"])) {
            Action.getRegularPaymentCashEntry(
                getQueryParameters()["subaccounthierarchyid"],
                CashPaymentTypeQuery.Deposit,
                CashPaymentFrequencyQuery.Regular,
                (data) => {
                    this.setState({ existingRegularPayment: data });
                }
            );
        }
    }

    isValid(): boolean {
        const { data } = this.props;
        return (
            !this.getDisplayExceedIsaAllowanceError() &&
            !this.getDisplayExceedNextTaxYearIsaAllowanceError(IsaHelper.calculateNextTaxYearISAAllowance(data))
        );
    }

    renderAlert(
        displayExceedIsaAllowanceError: boolean,
        displayExceedNextTaxYearIsaAllowanceError: boolean,
        nextTaxYearRegularTotal: number
    ) {
        const { data } = this.props;
        if (displayExceedIsaAllowanceError) {
            return <Alert alertType={AlertType.Error} message={IsaContents.Error_ExceedIsaAllowance} />;
        }
        if (displayExceedNextTaxYearIsaAllowanceError) {
            return (
                <Alert
                    alertType={AlertType.Error}
                    message={`Cannnot contribute a combined total of more than £${
                        data.IsaAllowance?.Total?.Amount
                    } to your ISAs in subsequent years, Regular contributions totaling £${nextTaxYearRegularTotal}, would be made between ${JsonDateParser.getFormatDate(
                        addYears(JsonDateParser.toDate(data.IsaAllowance?.TaxYear?.StartDate), 1),
                        "dd/MMM/yyyy"
                    )} and ${JsonDateParser.getFormatDate(
                        addYears(JsonDateParser.toDate(data.IsaAllowance?.TaxYear?.EndDate), 1),
                        "dd/MMM/yyyy"
                    )}`}
                />
            );
        }
    }

    private getDisplayExceedIsaAllowanceError(): boolean {
        const { data } = this.props;
        return !IsaHelper.validateIsaAllowance(data, this.state.existingRegularPayment, this.props.isReview);
    }

    private getDisplayExceedNextTaxYearIsaAllowanceError(nextYearTotal: number) {
        const { data } = this.props;

        if (data.ClientRegularPayment !== null || data.ThirdPartyRegularPayment !== null) {
            return !IsaHelper.validateNextTaxYearIsaAllowance(data.IsaAllowance?.Total?.Amount, nextYearTotal);
        }
        return false;
    }
    public render(): JSX.Element {
        const { TaxYear, Remaining, Total, Used, Expected } = this.props.data.IsaAllowance;
        const { data } = this.props;
        const used = IsaHelper.updatedUsed(this.props.data, this.state.existingRegularPayment, this.props.isReview);
        const adjustTotal = Total.Amount ?? Remaining.Amount;
        const unused = (Remaining?.Amount ?? Total.Amount) - (used - (Used?.Amount ?? 0) - (Expected?.Amount ?? 0));
        const ratio = used / adjustTotal;
        const percentage = ratio > 1 ? 100 : ratio * 100;
        const startYear = JsonDateParser.getFormatDate(TaxYear.StartDate, "yyyy");
        const endYear = JsonDateParser.getFormatDate(TaxYear.EndDate, "yyyy");
        const displayExceedIsaAllowanceError = this.getDisplayExceedIsaAllowanceError();
        const nextTaxYearRegularTotal = IsaHelper.calculateNextTaxYearISAAllowance(data);
        const displayExceedNextTaxYearIsaAllowanceError = this.getDisplayExceedNextTaxYearIsaAllowanceError(
            nextTaxYearRegularTotal
        );
        const cssWrapper = classNames("isa-allowance-chart", this.props.cssWrapper);

        return (
            <>
                <div className={cssWrapper}>
                    <div className="isa-label">
                        <div className="used-allowance-label">
                            <span>{MoneyDisplayHelper.FromatMoneyDisplay(used, CurrencyCode.GBP)}</span>
                            <div className="allowance-title">Used allowance</div>
                        </div>
                        <div className="unused-allowance-label">
                            <span>{MoneyDisplayHelper.FromatMoneyDisplay(unused, CurrencyCode.GBP)}</span>
                            <div className="allowance-title">Unused allowance</div>
                        </div>
                    </div>
                    <div className="isa-allowance-bar">
                        <span className="fill" style={{ width: percentage + "%" }}></span>
                    </div>
                    <p className="isa-year">
                        ISA allowance {startYear}/{endYear}
                    </p>
                </div>
                {this.renderAlert(
                    displayExceedIsaAllowanceError,
                    displayExceedNextTaxYearIsaAllowanceError,
                    nextTaxYearRegularTotal
                )}
            </>
        );
    }
}
